import type { FunctionComponent, PropsWithChildren } from 'react';
import { Button, Dialog, Inline, Modal, radiusHelper, Text } from '@property.com.au/ui-kit';
import styled, { useTheme } from 'styled-components';
import { getCssVariableValue } from '../../helpers/getCssVariableValue';
import { convertHexStringToRGBA } from '../../helpers/convertHexStringToRGBA';

interface DialogModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  primaryButtonText: string;
  primaryButtonOnClick: () => void;
  secondaryButtonText?: string;
  secondaryButtonOnClick?: () => void;
  gap?: string;
  maxHeight?: string;
}

const StyledDialog = styled(Dialog)`
  border-radius: ${radiusHelper('extraLarge')};
`;

export const DialogModal: FunctionComponent<PropsWithChildren<DialogModalProps>> = ({
  opened,
  onClose,
  title,
  primaryButtonText,
  primaryButtonOnClick,
  secondaryButtonText,
  secondaryButtonOnClick,
  gap,
  maxHeight,
  children,
}) => {
  const theme = useTheme();
  const colourValue = getCssVariableValue(theme.palette.backgroundOverlayStatic);
  const backgroundColor = convertHexStringToRGBA(colourValue, 0.3);

  const isSecondaryButtonVisible = secondaryButtonText && secondaryButtonOnClick;

  return (
    <Modal
      opened={opened}
      getAppElement={() => '#argonaut-wrapper'}
      onRequestClose={onClose}
      reactModalProps={{
        role: 'presentation',
        style: {
          overlay: {
            backgroundColor,
          },
          content: {
            width: '100%',
            maxHeight: maxHeight ?? '540px',
            display: 'flex',
            justifyContent: 'center',
          },
        },
      }}
    >
      <StyledDialog
        data-testid="modal-dialog"
        header={
          <Text as="h2" variant="subtitle01">
            {title}
          </Text>
        }
        footer={
          <Inline inset="extraSmall" grow={false} justifyContent="flex-end" gap="extraSmall">
            {isSecondaryButtonVisible && (
              <Button variant="outline" sizeVariant="small" onClick={secondaryButtonOnClick}>
                {secondaryButtonText}
              </Button>
            )}
            <Button variant="primary" sizeVariant="small" onClick={primaryButtonOnClick}>
              {primaryButtonText}
            </Button>
          </Inline>
        }
        onClose={onClose}
        showDialogBodyOverlay
        gap={gap}
        dangerouslySetClassNames={{ footer: 'footer' }}
      >
        {children}
      </StyledDialog>
    </Modal>
  );
};
